import { taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import { useGlobalManagerApproval } from "@mibuildweb/components";

export const useOrdersListActionMenuViewModel = ({ onRefresh }) => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const { executeApiRequest } = useGlobalManagerApproval();

  const rescheduleOrder = (orderId, data) => {
    return stateManager.run(async () => {
      const options = await taskService.order.getRescheduleOptions({
        orderId,
        data,
      });
      const response = await executeApiRequest({
        options,
        callback: onRefresh,
        noInitialErrorMessage: true,
      });
      if (response.status === HTTP_STATUSES.OK) {
        toastService.success({ text: "Order rescheduled successfully." });
        onRefresh();
      }
      return response.status === HTTP_STATUSES.OK;
    });
  };
  const holdOrder = (orderId, data) => {
    return stateManager.run(async () => {
      const options = await taskService.order.getHoldOptions({
        orderId,
        data,
      });
      const response = await executeApiRequest({
        options,
        callback: onRefresh,
        noInitialErrorMessage: true,
      });
      if (response.status === HTTP_STATUSES.OK) {
        toastService.success({ text: "Order was held successfully." });
        onRefresh();
      }
      return response.status === HTTP_STATUSES.OK;
    });
  };
  const cancelOrder = (orderId, { reason, modifiedOn }) => {
    return stateManager.run(async () => {
      const options = await taskService.order.getCancelOptions({
        orderId,
        data: {
          reason,
        },
        modifiedOn,
      });
      const response = await executeApiRequest({
        options,
        callback: onRefresh,
        noInitialErrorMessage: true,
      });
      if (response.status === HTTP_STATUSES.OK) {
        toastService.success({ text: "Order was cancelled successfully." });
        onRefresh();
      }
      return response.status === HTTP_STATUSES.OK;
    });
  };

  const forceCompleteOrder = ({ orderId, modifiedOn }) =>
    stateManager.run(async () => {
      const options = await taskService.order.getForceCompleteOptions({
        orderId,
        modifiedOn,
      });
      const response = await executeApiRequest({
        options,
        callback: onRefresh,
        noInitialErrorMessage: true,
      });
      if (response.status === HTTP_STATUSES.OK) {
        toastService.success({
          text: "Order was force completed successfully.",
        });
        onRefresh();
      }
    });

  const push24hOrder = (orderId, modifiedOn) => {
    return stateManager.run(async () => {
      const options = await taskService.order.getPushOptions({
        orderId,
        modifiedOn,
      });
      const response = await executeApiRequest({
        options,
        callback: onRefresh,
        noInitialErrorMessage: true,
      });
      if (response.status === HTTP_STATUSES.OK) {
        toastService.success({ text: "Order was pushed 24h successfully." });
        onRefresh();
      }
      return response.status === HTTP_STATUSES.OK;
    });
  };

  const undoConfirmation = (orderId, { reason, modifiedOn }) => {
    return stateManager.run(async () => {
      const options = await taskService.order.getUndoConfirmOptions({
        orderId,
        data: {
          reason,
        },
        modifiedOn,
      });

      const response = await executeApiRequest({
        options,
        callback: onRefresh,
        noInitialErrorMessage: true,
      });

      if (response.status === HTTP_STATUSES.OK) {
        toastService.success({
          text: "Order confirmation was undone successfully.",
        });
        onRefresh();
      }
    });
  };

  const undoAssignment = (orderId, { reason, modifiedOn }) => {
    return stateManager.run(async () => {
      const options = await taskService.order.getUndoAssignOptions({
        orderId,
        data: {
          reason,
        },
        modifiedOn,
      });

      const response = await executeApiRequest({
        options,
        callback: onRefresh,
        noInitialErrorMessage: true,
      });

      if (response.status === HTTP_STATUSES.OK) {
        toastService.success({
          text: "Order assignment was undone successfully.",
        });
        onRefresh();
      }
    });
  };

  const undoCompletion = (orderId, { reason, modifiedOn }) => {
    return stateManager.run(async () => {
      const options = await taskService.order.getUndoCompletionOptions({
        orderId,
        data: {
          reason,
        },
        modifiedOn,
      });

      const response = await executeApiRequest({
        options,
        callback: onRefresh,
        noInitialErrorMessage: true,
      });

      if (response.status === HTTP_STATUSES.OK) {
        toastService.success({
          text: "Order completion was undone successfully.",
        });
        onRefresh();
      }
    });
  };

  const undoCancel = (orderId, { reason, modifiedOn }) => {
    return stateManager.run(async () => {
      const options = await taskService.order.getUndoCancelOptions({
        orderId,
        data: {
          reason,
        },
        modifiedOn,
      });

      const response = await executeApiRequest({
        options,
        callback: onRefresh,
        noInitialErrorMessage: true,
      });

      if (response.status === HTTP_STATUSES.OK) {
        toastService.success({
          text: "Order cancellation was undone successfully.",
        });
        onRefresh();
      }
    });
  };

  const undoApprove = (orderId, { reason, modifiedOn }) => {
    return stateManager.run(async () => {
      const options = await taskService.order.getUndoApproveOptions({
        orderId,
        data: {
          reason,
        },
        modifiedOn,
      });

      const response = await executeApiRequest({
        options,
        callback: onRefresh,
        noInitialErrorMessage: true,
      });

      if (response.status === HTTP_STATUSES.OK) {
        toastService.success({
          text: "Order approval was undone successfully.",
        });
        onRefresh();
      }
    });
  };

  return {
    isLoading,
    rescheduleOrder,
    holdOrder,
    cancelOrder,
    forceCompleteOrder,
    push24hOrder,
    undoConfirmation,
    undoApprove,
    undoAssignment,
    undoCompletion,
    undoCancel,
  };
};
